import { useMemo } from 'react'

import { matchFilterNumber, sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'
import type { UseLoadMorePayload } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import { LoginSessionRequestOptions } from '@models/types'

const watchEntityKeys = ['loginSessions']

type LoginSessionsFilters = {
  user_id?: number,
}

interface UseEntitiesOptions {
  entityKey?: string,
  filters?: LoginSessionsFilters,
  performHttpRequests?: boolean,
  requestOptions?: LoginSessionRequestOptions,
}

function useLoginSessions(options: UseEntitiesOptions = {}) {
  const { filters = {}, requestOptions } = options
  const { user_id: filterUserId } = filters

  const {
    updatedEntities: { loginSessions: loginSessionsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const loginSessions = useSelector(reduxState => reduxState.entities.loginSessions)

  const filteredLoginSessions = useMemo(() => {
    const filtered = Object.values(loginSessions).filter((loginSession) => {
      const { user_id } = loginSession

      const matchUserId = matchFilterNumber(user_id, filterUserId)

      return matchUserId
    })

    return sortArrayBy(filtered, 'desc', 'id')
  }, [loginSessionsUpdatedAt, JSON.stringify(filters)])

  const filteredLoginSessionsCount = filteredLoginSessions.length
  const hasFilteredLoginSessions = !!filteredLoginSessionsCount

  const loadMorePayload: UseLoadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredLoginSessionsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingLoginSessions } = useReduxAction(
    'loginSessions',
    'loadLoginSessions',
    {
      limit,
      ...filtersWithOffset,
      ...requestOptions,
    },
    [filtersWithOffset, performHttpRequests],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => !!performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredLoginSessions,
    filteredLoginSessionsCount,
    hasFilteredLoginSessions,
    loading: loadingLoginSessions,
  }
}

export default useLoginSessions
